// The insides of buildings — Joseph's home, the workshop store, a neighbour's
// house. Pure data (no engine import), so verify.ts can assert every room's
// geometry headlessly under Node, the same way it does for world.ts.
//
// COORDINATE SPACE: interiors are laid out in ordinary world coordinates, in a
// strip well below the outdoor map (y >= INTERIOR_Y). Nothing can walk there
// from outside, and in exchange every system that already works in world space
// — the camera and its bounds, px/py, blocked(), depth sorting, d.visible()
// culling — keeps working indoors with no translation layer at all.
import type { MaterialKey } from './data.js';

/** Where the interior strip begins. WORLD_H is 1920; this leaves a wide gap. */
export const INTERIOR_Y = 3000;

export type VenueId = 'home' | 'shop' | 'hannah';

export interface Rect { x: number; y: number; w: number; h: number; }

/** Every piece of furniture a room can hold. Drawn by src/assets/interior.ts. */
export type FurnitureKind =
  | 'hearth' | 'mats' | 'table' | 'cushion' | 'jar' | 'basket' | 'chest'
  | 'loom' | 'grindstone' | 'shelf' | 'toolwall' | 'rack' | 'niche'
  | 'stool' | 'boards' | 'barrel' | 'toy' | 'herbs' | 'window' | 'benchTop' | 'rug'
  | 'mat' | 'shelfWall' | 'firewood' | 'bench' | 'potsFloor' | 'waterstand'
  | 'hangings' | 'beams' | 'sack';

export interface Furniture {
  kind: FurnitureKind;
  /** Footprint in world coords. Height is the visual depth on the floor. */
  x: number; y: number; w: number; h: number;
  /** Blocks Joseph. Low things (mats, cushions, a rug) deliberately do not. */
  solid?: boolean;
  /** Only drawn once the workshop has reached this level. */
  minLevel?: number;
  /** Only drawn once this request has been DELIVERED — the furniture Joseph
   *  actually built, appearing in his own home. */
  requires?: string;
  /** Drawn last, over everything: roof beams overhead. */
  overhead?: boolean;
  /** Deterministic variation, the way render.ts seeds everything else. */
  seed?: number;
}

/** A thing Joseph can stand near and press X on, inside a room. */
export interface InteriorSpot {
  id: 'bed' | 'table' | 'niche' | 'mary' | 'jesus' | 'resident' | 'stock';
  x: number; y: number;
  prompt: string;
}

export interface Interior {
  id: VenueId;
  /** Shown on the location banner in place of regionAt(). */
  name: string;
  /** The floor. Also the camera bounds — the engine centres rooms smaller
   *  than the view, so no special-casing is needed for a small room. */
  room: Rect;
  /** Which wall the door sits in, and the gap in that wall. */
  doorSide: 'north' | 'south' | 'east' | 'west';
  door: Rect;
  /** Standing on this mat, from inside, steps back out. */
  mat: Rect;
  /** Where Joseph appears when he walks in. */
  entry: { x: number; y: number };
  furniture: Furniture[];
  spots: InteriorSpot[];
}

// Rooms are authored at roughly VIEW scale (the view is worldHeight 768 tall,
// ~1230 wide at 16:9). There is no public camera zoom — the engine's is marked
// internal and explicitly not for game code — so a small room would sit in a
// void in the middle of the frame. They are stacked down the interior strip so
// no two can ever overlap.

// ── Joseph's home ────────────────────────────────────────────────────────────
// Door in the EAST wall: outside, Joseph always arrives at his own front door
// from the east, walking back from the workshop. The door is placed to meet
// him rather than making him round the building.
const HOME: Interior = {
  id: 'home',
  name: 'Home',
  room: { x: 200, y: INTERIOR_Y, w: 1120, h: 720 },
  doorSide: 'south',
  // The door sits east of the table, so the walk from it to the sleeping mats
  // is a clear run up the room and across the top. It used to open straight
  // onto the table, and the obvious route dead-ended against the hearth.
  door: { x: 852, y: INTERIOR_Y + 700, w: 116, h: 20 },
  mat: { x: 858, y: INTERIOR_Y + 630, w: 104, h: 68 },
  entry: { x: 906, y: INTERIOR_Y + 570 },
  furniture: [
    // ── overhead: roof beams, drawn last so the room has a ceiling
    { kind: 'beams', x: 226, y: INTERIOR_Y + 26, w: 1068, h: 668, overhead: true },

    // ── north wall: the window, herbs drying, the prayer niche
    { kind: 'window', x: 300, y: INTERIOR_Y + 4, w: 190, h: 26 },
    { kind: 'hangings', x: 560, y: INTERIOR_Y + 30, w: 150, h: 48 },
    { kind: 'herbs', x: 640, y: INTERIOR_Y + 6, w: 84, h: 46 },
    { kind: 'niche', x: 1104, y: INTERIOR_Y + 40, w: 124, h: 94, solid: true },

    // ── the sleeping corner: three mats, because there are three of them.
    // Joseph's and Mary's are separate; the small one is the boy's.
    { kind: 'mat', x: 250, y: INTERIOR_Y + 74, w: 152, h: 100, seed: 11 },
    { kind: 'mat', x: 414, y: INTERIOR_Y + 74, w: 152, h: 100, seed: 23 },
    { kind: 'mat', x: 580, y: INTERIOR_Y + 96, w: 98, h: 72, seed: 37 },

    // ── the hearth wall, where Mary works
    { kind: 'hearth', x: 232, y: INTERIOR_Y + 250, w: 150, h: 170, solid: true },
    { kind: 'potsFloor', x: 396, y: INTERIOR_Y + 392, w: 92, h: 58, seed: 5 },
    { kind: 'bench', x: 400, y: INTERIOR_Y + 300, w: 132, h: 62, solid: true, seed: 8 },
    { kind: 'firewood', x: 248, y: INTERIOR_Y + 444, w: 112, h: 68, solid: true, seed: 2 },
    { kind: 'grindstone', x: 248, y: INTERIOR_Y + 520, w: 108, h: 84, solid: true },

    // ── the middle of the room is LIVED IN, not left as a hall
    { kind: 'rug', x: 520, y: INTERIOR_Y + 380, w: 400, h: 210, seed: 4 },
    { kind: 'table', x: 570, y: INTERIOR_Y + 330, w: 268, h: 132, solid: true },
    { kind: 'cushion', x: 512, y: INTERIOR_Y + 300, w: 74, h: 46, seed: 3 },
    { kind: 'cushion', x: 856, y: INTERIOR_Y + 326, w: 74, h: 46, seed: 7 },
    { kind: 'cushion', x: 636, y: INTERIOR_Y + 486, w: 74, h: 46, seed: 5 },

    // ── the right of the room was bare floor; a household spreads out
    { kind: 'bench', x: 920, y: INTERIOR_Y + 170, w: 150, h: 62, solid: true, seed: 14 },
    { kind: 'potsFloor', x: 900, y: INTERIOR_Y + 300, w: 92, h: 58, seed: 21 },
    { kind: 'sack', x: 1046, y: INTERIOR_Y + 506, w: 70, h: 76, solid: true, seed: 17 },
    { kind: 'basket', x: 1216, y: INTERIOR_Y + 556, w: 78, h: 62, solid: true },

    // ── the east side: the loom, wool, the water jar on its stand
    { kind: 'loom', x: 1128, y: INTERIOR_Y + 230, w: 122, h: 160, solid: true },
    { kind: 'basket', x: 1084, y: INTERIOR_Y + 416, w: 80, h: 64, solid: true },
    { kind: 'waterstand', x: 1160, y: INTERIOR_Y + 452, w: 86, h: 98, solid: true },

    // ── the south wall: the household's stores
    { kind: 'jar', x: 420, y: INTERIOR_Y + 600, w: 64, h: 74, solid: true, seed: 2 },
    { kind: 'jar', x: 494, y: INTERIOR_Y + 612, w: 54, h: 62, solid: true, seed: 9 },
    { kind: 'sack', x: 574, y: INTERIOR_Y + 604, w: 72, h: 78, solid: true, seed: 6 },
    { kind: 'basket', x: 946, y: INTERIOR_Y + 606, w: 80, h: 64, solid: true },

    // ── JOSEPH'S OWN WORK. Neither of these is here on day one: they appear
    // only once he has actually built and given them to Mary.
    { kind: 'shelfWall', x: 830, y: INTERIOR_Y + 36, w: 196, h: 58, requires: 'shelf_home' },
    { kind: 'chest', x: 1040, y: INTERIOR_Y + 574, w: 150, h: 94, solid: true, requires: 'chest_home' },
  ],
  spots: [
    { id: 'bed', x: 325, y: INTERIOR_Y + 196, prompt: 'Rest for the night' },
    { id: 'table', x: 704, y: INTERIOR_Y + 492, prompt: 'Share the evening meal' },
    { id: 'niche', x: 1166, y: INTERIOR_Y + 142, prompt: 'Pray with the family' },
    { id: 'mary', x: 424, y: INTERIOR_Y + 392, prompt: 'Speak with Mary' },
    { id: 'jesus', x: 622, y: INTERIOR_Y + 200, prompt: 'Sit with young Jesus' },
  ],
};

// ── The workshop store ───────────────────────────────────────────────────────
// A walled room behind the open-air shop. The shop itself is untouched; this is
// where the timber, the tools and the finished pieces live. It fills out with
// the workshop's level, mirroring the way the outside grows.
const SHOP: Interior = {
  id: 'shop',
  name: 'The Workshop Store',
  room: { x: 200, y: INTERIOR_Y + 800, w: 1000, h: 660 },
  doorSide: 'east',
  door: { x: 1180, y: INTERIOR_Y + 1060, w: 20, h: 120 },
  mat: { x: 1108, y: INTERIOR_Y + 1072, w: 72, h: 96 },
  entry: { x: 1010, y: INTERIOR_Y + 1120 },
  furniture: [
    { kind: 'window', x: 400, y: INTERIOR_Y + 804, w: 120, h: 20 },
    // always here: rough boards leaning against the wall
    { kind: 'boards', x: 240, y: INTERIOR_Y + 860, w: 90, h: 220, solid: true, seed: 4 },
    // 2: the tool wall
    { kind: 'toolwall', x: 500, y: INTERIOR_Y + 812, w: 260, h: 60, minLevel: 2 },
    // 3: timber set to season
    { kind: 'rack', x: 960, y: INTERIOR_Y + 870, w: 130, h: 170, solid: true, minLevel: 3, seed: 6 },
    // 4: shelves of stock, and the glue barrel
    { kind: 'shelf', x: 250, y: INTERIOR_Y + 1180, w: 190, h: 60, solid: true, minLevel: 4 },
    { kind: 'barrel', x: 520, y: INTERIOR_Y + 1180, w: 66, h: 72, solid: true, minLevel: 4 },
    // 5: the stone corner
    { kind: 'grindstone', x: 980, y: INTERIOR_Y + 1180, w: 84, h: 68, solid: true, minLevel: 5 },
    // 6: finished pieces waiting to be delivered — read from st.products
    { kind: 'benchTop', x: 420, y: INTERIOR_Y + 980, w: 210, h: 80, solid: true, minLevel: 6 },
    // 7: a lamp-lit, ordered shop, and a stool where the boy sits and watches
    { kind: 'stool', x: 700, y: INTERIOR_Y + 1020, w: 48, h: 50, minLevel: 7 },
  ],
  spots: [
    { id: 'stock', x: 520, y: INTERIOR_Y + 1080, prompt: 'Look over the finished work' },
  ],
};

// ── Hannah's house ───────────────────────────────────────────────────────────
// The first request in the game is Hannah's stool, so hers is the neighbour's
// door worth opening. Approached from the east, off the well.
const HANNAH: Interior = {
  id: 'hannah',
  name: "Hannah's House",
  room: { x: 200, y: INTERIOR_Y + 1540, w: 940, h: 620 },
  doorSide: 'east',
  door: { x: 1120, y: INTERIOR_Y + 1820, w: 20, h: 100 },
  mat: { x: 1062, y: INTERIOR_Y + 1830, w: 60, h: 84 },
  entry: { x: 1000, y: INTERIOR_Y + 1960 },
  furniture: [
    { kind: 'window', x: 380, y: INTERIOR_Y + 1544, w: 110, h: 20 },
    { kind: 'hearth', x: 232, y: INTERIOR_Y + 1700, w: 100, h: 120, solid: true },
    { kind: 'mats', x: 400, y: INTERIOR_Y + 1590, w: 220, h: 95, seed: 21 },
    { kind: 'table', x: 560, y: INTERIOR_Y + 1780, w: 170, h: 90, solid: true },
    { kind: 'cushion', x: 530, y: INTERIOR_Y + 1750, w: 55, h: 34, seed: 13 },
    { kind: 'jar', x: 900, y: INTERIOR_Y + 1600, w: 44, h: 52, solid: true, seed: 17 },
    { kind: 'jar', x: 950, y: INTERIOR_Y + 1610, w: 38, h: 46, solid: true, seed: 19 },
    { kind: 'basket', x: 300, y: INTERIOR_Y + 2000, w: 62, h: 48, solid: true },
    { kind: 'toy', x: 700, y: INTERIOR_Y + 2020, w: 32, h: 26 },
    { kind: 'loom', x: 990, y: INTERIOR_Y + 1700, w: 85, h: 115, solid: true },
  ],
  spots: [
    { id: 'resident', x: 640, y: INTERIOR_Y + 1880, prompt: 'Look about the room' },
  ],
};

export const INTERIORS: Interior[] = [HOME, SHOP, HANNAH];

export function interiorById(id: VenueId): Interior | undefined {
  return INTERIORS.find((i) => i.id === id);
}

// ── Doors on the outdoor map ─────────────────────────────────────────────────
// Walking onto a threshold enters; there is no key press and no prompt, because
// a door you have to ASK to open is a door that feels like a chore. Each one
// sits against the face of its building that Joseph actually walks in from.
export interface DoorDef {
  to: VenueId;
  /** Walk INTO this, heading inward, and you go in. */
  threshold: Rect;
  /** The way you must be travelling for the doorway to take you. Walking past
   *  a door, or stepping away from one you just came out of, must not drag you
   *  back inside — so entry is directional, not mere contact. */
  enter: { x: number; y: number };
  /** Where Joseph is put when he comes back out. Placed well clear of the
   *  threshold along the OUTWARD normal, so leaving never lands you on the
   *  trigger you just used. */
  exit: { x: number; y: number };
}

export const DOORS: DoorDef[] = [
  // Home: the doorway at the right-hand end of the front wall (x300-600 /
  // y980-1220). Joseph comes home from the workshop, so he meets it head-on;
  // you enter heading NORTH.
  { to: 'home', threshold: { x: 508, y: 1198, w: 80, h: 34 },
    enter: { x: 0, y: -1 }, exit: { x: 548, y: 1302 } },

  // The timber store (x736-926 / y934-1052). Its door used to face SOUTH,
  // straight onto the only walking route from the compound to the grove — so
  // every trip north dragged Joseph inside. It now faces EAST, toward the byre
  // he is coming from anyway, leaving the north-south corridor clear.
  { to: 'shop', threshold: { x: 928, y: 978, w: 34, h: 56 },
    enter: { x: -1, y: 0 }, exit: { x: 1032, y: 1006 } },

  // Hannah's house (x1180-1410 / y760-960), approached from the well. Hers is
  // the one `solid: true` building of the three, and blocked() throws a padded
  // blocker out to x1405.6 — right across the rendered doorway — so the
  // threshold starts clear of that at 1410, not flush with the wall.
  { to: 'hannah', threshold: { x: 1412, y: 876, w: 34, h: 52 },
    enter: { x: -1, y: 0 }, exit: { x: 1524, y: 902 } },
];

export function doorTo(id: VenueId): DoorDef | undefined {
  return DOORS.find((d) => d.to === id);
}

/** True if (x, y) sits inside a rect. Shared by the threshold and mat tests. */
export function inRect(x: number, y: number, r: Rect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

/** Materials a finished piece on the store shelf might have been made from —
 *  used only to tint the stacked products, never for gameplay. */
export const STOCK_TINTS: MaterialKey[] = ['wood', 'timber', 'stone'];
